import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Badge,
  Button,
  Icon,
  InputAdornment,
  FormHelperText,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {
  updateMultipleOrdersPostedStatusApi,
  generateOrdersInvoicePdfApi,
  holdOrdersApi,
  unholdOrdersApi
} from 'api';
import { DesignedConfirmDialog } from 'components';
import ViewColumn from 'helpers/ViewColumn';


const useStyles = makeStyles({
  bgBlue: {
    backgroundColor: '#E7F0FF',
  },
  customSearch: {
    borderTopLeftRadius: '6px',
    borderBottomLeftRadius: '6px',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E7F0FF'
    },
    '& .MuiInputBase-input::-webkit-input-placeholder': {
      color: '#4B6FC2',
      opacity: '1'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4B6FC2'
    }
  },
  customSearchField: {
    borderTopRightRadius: '6px',
    borderBottomRightRadius: '6px',

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E7F0FF'
    },
    '& .MuiInputBase-input::-webkit-input-placeholder': {
      color: '#4B6FC2',
      opacity: '1'
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#4B6FC2'
    },
    '& .MuiInputAdornment-root .MuiTypography-colorTextSecondary': {
      color: '#4B6FC2'
    }
  },
  helperText: {
    position: 'absolute',
    bottom: '-15px',
    left: '25px'
  },
  filterBtn: {
    padding: '7px 15px'
  }
});

interface ILocationStateType {
  orderId: string
}

const CustomTableToolbar = (
  {
    selectedIncrementIds,
    onSearch,
    setOpenFilter,
    filterConditions,
    tableCells,
    setHeadTableCells,
    hiddenFilters = {},
    searchConditions = {}
  }: ICustomTableToolbar
) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { state } = useLocation<ILocationStateType>();
  const {field, value} = searchConditions as IFilterCondition;
  const [confirmDialog, setConfirmDialog] = useState({ open: false, checked: false, action: '' });
  const [searchValue, setSearchValue] = useState(value ? value.replaceAll('%', '') : '');
  const [searchField, setSearchField] = useState(field || 'increment_id');
  const [ordersOnHold, setOrdersOnHold] = useState(false);
  const [ordersUnposted, setOrdersUnposted] = useState(false);
  const selectedConditions = Object.values(filterConditions)
    .filter((condition) => (!!condition?.value.length && condition.field !== 'emr_sync_error') && //'emr_sync_error' should be invisible for user
     !Object.keys(hiddenFilters).includes(condition.field));
  const { storeOrders } = useSelector((state: IReduxState) => state.store_orders);
  const { storeSurveyQuestionConfigs } = useSelector((state: IReduxState) => state.store_configs);
  const isSurveyEnabled = storeSurveyQuestionConfigs.find((config) => config.path === 'surveyquestion/general/enable')?.value === '1';

  useEffect(() => {
    const selectedOrders = storeOrders.filter((o) => selectedIncrementIds.includes(o.increment_id));
    const haveOnHoldStatus = selectedOrders.every((order) => order.status === 'holded');
    const haveUnpostedStatus = selectedOrders.some(({extension_attributes: attr}) => !attr?.delivery_status || attr?.delivery_status === '0');

    setOrdersOnHold(haveOnHoldStatus);
    setOrdersUnposted(haveUnpostedStatus);
  }, [selectedIncrementIds]);

  useEffect(() => {
    if (state?.orderId) {
      setSearchValue(state?.orderId);
    }
  }, []);

  const handleChangeHoldStatus = () => {
    const { checked } = confirmDialog;
    const selectedOrders = storeOrders.filter((o) => selectedIncrementIds.includes(o.increment_id));

    const orderIds = selectedOrders.reduce((acc, order) => {
      if (order.status === 'holded' && !checked) {
        acc.push(order.entity_id);
      } else if (order.status !== 'holded' && checked) {
        acc.push(order.entity_id);
      }

      return acc;
    }, []);

    if (!checked) {
      unholdOrdersApi({dispatch, orderIds});
    } else {
      holdOrdersApi({dispatch, orderIds});
    }

    setOrdersOnHold(checked);
  };

  const handleChangePostedStatus = () => {
    const { checked } = confirmDialog;
    const selectedOrders = storeOrders.filter((o) => selectedIncrementIds.includes(o.increment_id));

    const orders = selectedOrders.reduce((acc, o) => {
      const { delivery_status } = o.extension_attributes;
      let newDeliveryStatus;

      if (delivery_status === '1' && !checked) {
        newDeliveryStatus = '0';
      } else if ((!delivery_status || delivery_status === '0') && checked) {
        newDeliveryStatus = '1';
      }

      if (newDeliveryStatus) {
        acc.push({
          increment_id: o.increment_id,
          entity_id: o.entity_id,
          extension_attributes: {
            delivery_status: newDeliveryStatus
          }
        });
      }

      return acc;
    }, []);

    updateMultipleOrdersPostedStatusApi({dispatch, orders});
    setOrdersUnposted(!checked);
  };

  const handleGeneratePdf = () => {
    const orderIds = storeOrders.filter((o) => selectedIncrementIds.includes(o.increment_id)).map((o) => o.entity_id);

    generateOrdersInvoicePdfApi({dispatch, orderIds});
  };

  const handleConfirmAction = () => {
    const { action } = confirmDialog;

    if (['HOLD', 'UNHOLD'].includes(action)) {
      handleChangeHoldStatus();
    } else if (['POST', 'UNPOST'].includes(action)) {
      handleChangePostedStatus();
    } else if (action === 'PRINT INVOICES for') {
      handleGeneratePdf();
    }
  };

  return (
    <Toolbar className="w-full relative">
      {selectedIncrementIds.length > 0 ? (
        <div className="w-full flex justify-between">
          <Typography color="inherit" variant="subtitle1" component="div">
            {selectedIncrementIds.length} selected
          </Typography>
          <div>
            {ordersOnHold &&
              <Button
                variant="contained"
                color="primary"
                className="mr-2 w-120"
                onClick={() => setConfirmDialog({ open: true, checked: !ordersOnHold, action: ordersOnHold ? 'UNHOLD' : 'HOLD'})}
              >
                <span>{ordersOnHold ? 'UNHOLD' : 'HOLD'}</span>
              </Button>}
            <Button
              variant="contained"
              color="primary"
              className="mr-2 w-120"
              onClick={() => setConfirmDialog({ open: true, checked: ordersUnposted, action: ordersUnposted ? 'POST' : 'UNPOST' })}
            >
              <span>{ordersUnposted ? 'POST' : 'UNPOST'}</span>
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setConfirmDialog({ open: true, checked: true, action: 'PRINT INVOICES for' })}
            >
              <Icon className="mr-2">print_outlined</Icon>
              <span>PRINT INVOICES</span>
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-column w-full">
          <div className="flex items-center w-full">
            <TextField
              variant="outlined"
              className={`w-full max-w-300 text-primary ${classes.bgBlue} ${classes.customSearch}`}
              name="search-orders-value"
              placeholder="Search"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onSearch(searchValue, searchField);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {!searchValue.length ?
                      <Icon color="primary">search</Icon>
                      :
                      <Icon
                        color="primary"
                        className="cursor-pointer"
                        onClick={() => {
                          setSearchValue(() => '');
                          onSearch('', 'increment_id');
                        }}
                      >
                        close
                      </Icon>
                    }
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              variant="outlined"
              className={`w-full max-w-200 mr-3 text-primary ${classes.bgBlue} ${classes.customSearchField}`}
              name="search-orders-field"
              value={searchField}
              onChange={(event) => {
                setSearchField(event.target.value);

                if (searchValue) {
                  onSearch(searchValue, event.target.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">by</InputAdornment>
                )
              }}
            >
              <MenuItem value="increment_id">Order Id</MenuItem>
              <MenuItem value="customer_firstname">First Name</MenuItem>
              <MenuItem value="customer_lastname">Last Name</MenuItem>
              <MenuItem value="customer_email">Email</MenuItem>
              <MenuItem value="status">Status</MenuItem>
              {isSurveyEnabled && <MenuItem value="surveyquestion">Survey</MenuItem>}
            </TextField>
            <Button
              color="primary"
              variant="contained"
              className="mr-3 px-3 min-w-100 min-h-40"
              onClick={() => onSearch(searchValue, searchField)}
            >
              <Icon className="mr-2">search</Icon>
              <span className="uppercase">Search</span>
            </Button>
            <Badge
              badgeContent={selectedConditions.length}
              color="primary"
              overlap="rectangular"
            >
              <Tooltip title="Filter by">
                <Button
                  variant="outlined"
                  color="primary"
                  className={classes.filterBtn}
                  onClick={() => setOpenFilter(true)}
                >
                  <Icon className="mr-2 text-primary">
                    filter_list
                  </Icon>
                  <span className="text-brand">Filter By</span>
                </Button>
              </Tooltip>
            </Badge>
          </div>
          {!!searchValue.length &&
            <FormHelperText className={classes.helperText}>
              <Typography variant="caption">
                Press &apos;Enter&apos; to search
              </Typography>
            </FormHelperText>
          }
        </div>
      )}
      <ViewColumn storageKey="OrderTableCells" tableCells={tableCells} setHeadTableCells={setHeadTableCells} />
      <DesignedConfirmDialog
        open={confirmDialog.open}
        setOpen={(open) => setConfirmDialog({ open, checked: false, action: '' })}
        onConfirm={handleConfirmAction}
      >
        {`Do you want ${confirmDialog.action} selected orders?`}
      </DesignedConfirmDialog>
    </Toolbar>
  );
};

export default CustomTableToolbar;
